import { useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import type { Control, FieldErrors } from 'react-hook-form';

import { OptionalField, isOptionalFieldKey } from '@/features/optional-fields';
import type { OptionalFieldConfig } from '@/entities/layout';

import type { ConsignmentLineDetailsFormValues } from '../model/types';

interface OptionalFieldsSectionProps {
  control: Control<ConsignmentLineDetailsFormValues>;
  errors: FieldErrors<ConsignmentLineDetailsFormValues>;
  optionalFields?: OptionalFieldConfig[] | null;
  isViewMode: boolean;
}

export const OptionalFieldsSection = ({ control, errors, optionalFields, isViewMode }: OptionalFieldsSectionProps) => {
  const formValues = useWatch({ control });

  const fieldsToRender = useMemo(() => {
    if (!optionalFields?.length) return [];

    return optionalFields
      .map((fieldConfig) => {
        const [[fieldKey, definition]] = Object.entries(fieldConfig);

        if (!definition || !isOptionalFieldKey(fieldKey)) return null;

        if (isViewMode) {
          const fieldValue = formValues[fieldKey];
          if (!fieldValue) return null;
        }

        return { fieldKey, definition };
      })
      .filter(Boolean);
  }, [optionalFields, isViewMode, formValues]);

  if (!optionalFields?.length) return null;
  if (fieldsToRender.length === 0) return null;

  return (
    <>
      {fieldsToRender.map((field) => {
        if (!field) return null;
        const { fieldKey, definition } = field;

        return (
          <Controller
            key={fieldKey}
            name={fieldKey}
            control={control}
            render={({ field }) => (
              <OptionalField
                fieldKey={fieldKey}
                definition={definition}
                value={field.value || null}
                onChange={field.onChange}
                error={!isViewMode ? errors[fieldKey]?.message : undefined}
                disabled={isViewMode}
              />
            )}
          />
        );
      })}
    </>
  );
};
